'use client';

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  ChevronLeft,
  AlertCircle,
  Puzzle,
  ChevronDown,
  ChevronUp,
  Search,
  Sparkles,
  Calendar,
  X,
} from 'lucide-react';
import { lucideIcons } from '@/lib/lucideIcons';
import { useTranslation } from 'react-i18next';
import { InfoDialog } from '@/components/pages/dialogs/InfoDialog';
import { CategoryForm } from '@/components/pages/dialogs/EditCategoryDialog';
import { GenerateDescriptionDialog } from '@/components/pages/dialogs/GenerateDescriptionDialog';
import { AssignWorkersToServiceDialog } from '@/components/pages/dialogs/AssignWorkersToServiceDialog';
import { Servicio } from '@/components/hooks/useServicios';
import { useEditarServicios } from '@/components/hooks/useEditarServicios';
import { useCategoriasServicio } from '@/components/hooks/useCategoriasServicio';
import { useIA } from '@/components/hooks/useIA';
import {
  DLG_BTN_PRIMARY,
  DLG_BTN_SECONDARY,
} from '@/components/ui/dialogButtons';

const SHEET_CLASS = `
  z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
  [&>button:last-child]:hidden
  flex flex-col
  !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
  w-full max-w-none max-h-[94vh] rounded-t-2xl rounded-b-none
  data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
  sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
  sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:max-h-[94vh] sm:rounded-xl sm:shadow-xl
`;

// ============================================================
// Catálogo de iconos
// ============================================================
const SERVICE_ICONS: { name: string; label: string }[] = [
  { name: 'Scissors', label: 'Tijeras' },
  { name: 'Sparkles', label: 'Brillo' },
  { name: 'Heart', label: 'Corazón' },
  { name: 'Star', label: 'Estrella' },
  { name: 'Smile', label: 'Sonrisa' },
  { name: 'Sun', label: 'Sol' },
  { name: 'Flower2', label: 'Flor' },
  { name: 'Leaf', label: 'Hoja' },
  { name: 'Droplets', label: 'Gotas' },
  { name: 'Flame', label: 'Llama' },
  { name: 'Zap', label: 'Rayo' },
  { name: 'Wind', label: 'Viento' },
  { name: 'Eye', label: 'Ojo' },
  { name: 'Hand', label: 'Mano' },
  { name: 'Brush', label: 'Pincel' },
  { name: 'PaintBucket', label: 'Pintura' },
  { name: 'Syringe', label: 'Jeringa' },
  { name: 'Pill', label: 'Pastilla' },
  { name: 'Stethoscope', label: 'Estetoscopio' },
  { name: 'Dumbbell', label: 'Pesas' },
  { name: 'Bike', label: 'Bicicleta' },
  { name: 'PersonStanding', label: 'Persona' },
  { name: 'Bath', label: 'Baño' },
  { name: 'ShowerHead', label: 'Ducha' },
  { name: 'Coffee', label: 'Café' },
  { name: 'Music', label: 'Música' },
  { name: 'Camera', label: 'Cámara' },
  { name: 'Laptop', label: 'Portátil' },
  { name: 'Wrench', label: 'Herramienta' },
  { name: 'Car', label: 'Coche' },
  { name: 'Home', label: 'Casa' },
  { name: 'Building2', label: 'Edificio' },
  { name: 'Globe', label: 'Global' },
  { name: 'Clock', label: 'Reloj' },
  { name: 'Calendar', label: 'Calendario' },
  { name: 'Gift', label: 'Regalo' },
  { name: 'ShoppingBag', label: 'Bolsa' },
  { name: 'Palette', label: 'Paleta' },
  { name: 'Gem', label: 'Joya' },
  { name: 'Crown', label: 'Corona' },
];

function LucideIcon({ name, className }: { name: string; className?: string }) {
  const Icon = (lucideIcons as any)[name];
  if (!Icon) return null;
  return <Icon className={className ?? 'w-5 h-5'} />;
}

// ============================================================
// Helpers duración
// ============================================================
function parseDuracion(val: string) {
  const total = parseInt(val || '0', 10);
  const dias = Math.floor(total / 1440);
  const horas = Math.floor((total % 1440) / 60);
  const mins = total % 60;
  return { total, dias, horas, mins };
}

function buildDuracion(dias: number, horas: number, mins: number) {
  return String(dias * 1440 + horas * 60 + mins);
}

function formatDuracionResumen(total: number): string {
  if (total <= 0) return 'Sin duración';
  const dias = Math.floor(total / 1440);
  const horas = Math.floor((total % 1440) / 60);
  const mins = total % 60;
  const partes = [
    dias > 0 && `${dias}d`,
    horas > 0 && `${horas}h`,
    mins > 0 && `${mins}min`,
  ].filter(Boolean);
  return partes.join(' ');
}

// ============================================================
// DurationPicker
// ============================================================
function DurationPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (val: string) => void;
}) {
  const [showDays, setShowDays] = useState(false);
  const { total, dias, horas, mins } = parseDuracion(value);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => setShowDays((v) => !v)}
          title={showDays ? 'Ocultar días' : 'Añadir días'}
          className={`shrink-0 h-9 w-9 flex items-center justify-center rounded-lg border transition-colors ${
            dias > 0 || showDays
              ? 'border-[#103254] bg-[#103254]/10 text-[#103254] dark:border-blue-400 dark:bg-blue-400/10 dark:text-blue-400'
              : 'border-gray-300 dark:border-white/10 text-gray-400 hover:border-gray-400 dark:hover:border-white/20'
          }`}
        >
          <Calendar className="w-4 h-4" />
        </button>

        <div className="flex-1 flex flex-col items-center gap-0.5">
          <Select
            value={String(horas)}
            onValueChange={(v) =>
              onChange(buildDuracion(dias, parseInt(v, 10), mins))
            }
          >
            <SelectTrigger className="h-9 text-sm w-full focus:ring-[#103254]/30 dark:bg-white/5 dark:border-white/10 dark:text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[70]">
              {Array.from({ length: 24 }, (_, i) => (
                <SelectItem key={i} value={String(i)} className="text-sm">
                  {i}h
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <span className="text-gray-400 text-sm shrink-0">:</span>

        <div className="flex-1 flex flex-col items-center gap-0.5">
          <Select
            value={String(mins)}
            onValueChange={(v) =>
              onChange(buildDuracion(dias, horas, parseInt(v, 10)))
            }
          >
            <SelectTrigger className="h-9 text-sm w-full focus:ring-[#103254]/30 dark:bg-white/5 dark:border-white/10 dark:text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[70]">
              {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((m) => (
                <SelectItem key={m} value={String(m)} className="text-sm">
                  {m}min
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {showDays && (
        <div className="flex items-center gap-2 p-2.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg">
          <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
          <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
            Días:
          </span>
          <Select
            value={String(dias)}
            onValueChange={(v) =>
              onChange(buildDuracion(parseInt(v, 10), horas, mins))
            }
          >
            <SelectTrigger className="h-9 text-sm flex-1 focus:ring-[#103254]/30 dark:bg-white/5 dark:border-white/10 dark:text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[70]">
              {Array.from({ length: 31 }, (_, i) => (
                <SelectItem key={i} value={String(i)} className="text-sm">
                  {i} {i === 1 ? 'día' : 'días'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {total > 0 && (
        <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
          {formatDuracionResumen(total)}
          <span className="ml-1 text-gray-300 dark:text-gray-600">
            ({total} min)
          </span>
        </p>
      )}
    </div>
  );
}

// ============================================================
// IconPicker
// ============================================================
function IconPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selectedLabel =
    SERVICE_ICONS.find((ic) => ic.name === value)?.label ?? value;
  const normalize = (s: string) =>
    s
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  const filtered = search.trim()
    ? SERVICE_ICONS.filter(
        (ic) =>
          normalize(ic.label).includes(normalize(search)) ||
          normalize(ic.name).includes(normalize(search))
      )
    : SERVICE_ICONS;

  return (
    <div className="space-y-1.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-gray-300 dark:border-white/10 rounded-lg bg-white dark:bg-white/5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/10 transition-colors"
      >
        <span className="flex items-center gap-2">
          {value ? (
            <>
              <LucideIcon
                name={value}
                className="w-4 h-4 text-[#103254] dark:text-blue-400 shrink-0"
              />
              <span className="text-[#103254] dark:text-blue-400 font-medium">
                {selectedLabel}
              </span>
            </>
          ) : (
            <span className="text-gray-400">Sin icono</span>
          )}
        </span>
        {open ? (
          <ChevronUp className="w-4 h-4 shrink-0 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 shrink-0 text-gray-400" />
        )}
      </button>

      {open && (
        <div className="border border-gray-200 dark:border-white/10 rounded-lg bg-gray-50 dark:bg-white/5 p-2 space-y-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar icono…"
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-300 dark:border-white/10 rounded-md bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#103254] dark:focus:ring-blue-500"
            />
          </div>
          <div className="grid grid-cols-8 gap-1 max-h-44 overflow-y-auto pr-0.5">
            {!search.trim() && (
              <button
                type="button"
                title="Sin icono"
                onClick={() => {
                  onChange('');
                  setOpen(false);
                }}
                className={`flex items-center justify-center aspect-square rounded-md text-xs font-bold transition-colors ${
                  value === ''
                    ? 'bg-[#103254] text-white'
                    : 'text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10'
                }`}
              >
                —
              </button>
            )}
            {filtered.length === 0 && (
              <p className="col-span-8 text-center text-xs text-gray-400 py-3">
                Sin resultados
              </p>
            )}
            {filtered.map((ic) => (
              <button
                key={ic.name}
                type="button"
                title={ic.label}
                onClick={() => {
                  onChange(ic.name);
                  setOpen(false);
                }}
                className={`flex items-center justify-center aspect-square rounded-md transition-colors ${
                  value === ic.name
                    ? 'bg-[#103254] text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/10'
                }`}
              >
                <LucideIcon name={ic.name} className="w-4 h-4" />
              </button>
            ))}
          </div>
          {value && (
            <p className="flex items-center gap-1.5 text-xs text-[#103254] dark:text-blue-400 px-0.5">
              <LucideIcon name={value} className="w-3.5 h-3.5 shrink-0" />
              {selectedLabel}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Props
// ============================================================
type Props = {
  open: boolean;
  onClose: () => void;
  editingService: Servicio | null;
  empresaId: string;
  onSaved: (newServiceId?: string) => void;
  onEditPhases: (serviceId: string) => void;
};

// ============================================================
// Componente principal
// ============================================================
export const EditServiceDialog = ({
  open,
  onClose,
  editingService,
  empresaId,
  onSaved,
  onEditPhases,
}: Props) => {
  const { t } = useTranslation();

  const [assignWorkersOpen, setAssignWorkersOpen] = useState(false);
  const [servicioRecienCreado, setServicioRecienCreado] = useState<
    string | null
  >(null);

  const handleSavedIntercepted = (newServiceId?: string) => {
    if (!editingService && newServiceId && form.requiere_trabajador) {
      setServicioRecienCreado(newServiceId);
      setAssignWorkersOpen(true);
    } else {
      onSaved(newServiceId);
    }
  };

  const {
    form,
    setForm,
    handleInput,
    toggleDay,
    precioAplicaOptions,
    loadingForm,
    horas,
    newHora,
    setNewHora,
    addHora,
    removeHora,
    DAYS,
    showAdvanced,
    setShowAdvanced,
    showDays,
    setShowDays,
    showInfo,
    setShowInfo,
    showInfoPublico,
    setShowInfoPublico,
    showInfoActivo,
    setShowInfoActivo,
    showInfoPermiteGrupos,
    setShowInfoPermiteGrupos,
    showInfoRequiereTrabajador,
    setShowInfoRequiereTrabajador,
    showInfoDiasDisponibles,
    setShowInfoDiasDisponibles,
    showInfoHorarioConcreto,
    setShowInfoHorarioConcreto,
    crearFasesAhora,
    setCrearFasesAhora,
    advancedRef,
    daysRef,
    horariosRef,
    getDescripcion,
    handleSubmit,
    categoriasServicio,
    refetchCategorias,
  } = useEditarServicios(
    open,
    editingService,
    empresaId,
    handleSavedIntercepted,
    onClose,
    onEditPhases
  );

  const [showInfoCrearFases, setShowInfoCrearFases] = useState(false);
  const [showInfoPorHoras, setShowInfoPorHoras] = useState(false);
  const [showInfoReservable, setShowInfoReservable] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);

  useEffect(() => {
    if (open) setShowCategoryDialog(false);
  }, [open]);

  const {
    createCategoria: crearCategoriaSvc,
    updateCategoria: actualizarCategoriaSvc,
  } = useCategoriasServicio(empresaId);

  const {
    generarDescripcion,
    generarSiguiente,
    resetTono,
    loading: loadingIA,
  } = useIA();
  const [showIADialog, setShowIADialog] = useState(false);
  const [iaDescripcion, setIaDescripcion] = useState('');

  const handleGenerarDescripcion = async () => {
    if (!form.servicio) return;
    resetTono();
    const resultado = await generarDescripcion(
      'descripcion_servicio_simple' as any,
      {
        nombre_servicio: form.servicio,
        precio_servicio: form.precio || undefined,
        duracion_servicio: form.duracion_minutos || undefined,
      } as any
    );
    setIaDescripcion(resultado);
    setShowIADialog(true);
  };

  const handleSiguienteVariante = async () => {
    if (!form.servicio) return;
    const resultado = await generarSiguiente(
      'descripcion_servicio_simple' as any,
      {
        nombre_servicio: form.servicio,
        precio_servicio: form.precio || undefined,
        duracion_servicio: form.duracion_minutos || undefined,
      } as any
    );
    setIaDescripcion(resultado);
  };

  const handleCloseAssignWorkers = () => {
    setAssignWorkersOpen(false);
    setServicioRecienCreado(null);
    onSaved();
    onClose();
  };

  return (
    <>
      {/* ================================================
          DIÁLOGO PRINCIPAL
      ================================================= */}
      <Dialog open={open && !showInfo && !showIADialog && !assignWorkersOpen} onOpenChange={onClose}>
        <DialogContent className={SHEET_CLASS}>
          {loadingForm ? (
            <div className="h-40 flex justify-center items-center">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-gray-600 dark:border-gray-400" />
            </div>
          ) : (
            <>
              <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative flex items-center gap-2">
                {showCategoryDialog && (
                  <button
                    onClick={() => setShowCategoryDialog(false)}
                    className="shrink-0 -ml-1 w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                    aria-label="Volver"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                )}
                <DialogHeader className="flex-1 min-w-0">
                  <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10 truncate">
                    {showCategoryDialog
                      ? 'Nueva categoría'
                      : editingService
                      ? t('editServiceDialog.titleEdit')
                      : t('editServiceDialog.titleNew')}
                  </DialogTitle>
                </DialogHeader>
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                  aria-label="Cerrar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {showCategoryDialog ? (
                <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-5">
                  <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 text-sm text-gray-700 dark:text-gray-300">
                    <CategoryForm
                      isNew
                      createCategoria={crearCategoriaSvc}
                      updateCategoria={actualizarCategoriaSvc}
                      busyExternal={false}
                      onCancel={() => setShowCategoryDialog(false)}
                      onSaved={(nueva) => {
                        setForm((prev) => ({
                          ...prev,
                          id_categoria_servicio: nueva.id_categoria_servicio,
                        }));
                        refetchCategorias();
                        setShowCategoryDialog(false);
                      }}
                    />
                  </div>
                </div>
              ) : (
              <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-5">
                <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-6 text-sm">
                  {/* Nombre */}
                  <div>
                    <label className="block font-medium mb-1 text-gray-700 dark:text-gray-300">
                      {t('editServiceDialog.labelName')}
                    </label>
                    <Input
                      name="servicio"
                      value={form.servicio}
                      onChange={handleInput}
                      className="dark:bg-white/5 dark:border-white/10 dark:text-white"
                    />
                  </div>

                  {/* Categoría */}
                  <div>
                    <label className="block font-medium mb-1 text-gray-700 dark:text-gray-300">
                      Categoría
                    </label>
                    <div className="flex gap-2 w-full">
                      <div className="flex-1 min-w-0">
                        <Select
                          value={form.id_categoria_servicio || ''}
                          onValueChange={(v) =>
                            setForm((prev) => ({
                              ...prev,
                              id_categoria_servicio: v === '__none__' ? '' : v,
                            }))
                          }
                        >
                          <SelectTrigger className="w-full h-11 text-sm focus:ring-[#103254]/30 dark:bg-white/5 dark:border-white/10 dark:text-white">
                            <SelectValue placeholder="Sin categoría" />
                          </SelectTrigger>
                          <SelectContent className="z-[70]">
                            <SelectItem value="__none__" className="text-sm">
                              Sin categoría
                            </SelectItem>
                            {categoriasServicio.map((c) => (
                              <SelectItem
                                key={c.id_categoria_servicio}
                                value={c.id_categoria_servicio}
                                className="text-sm"
                              >
                                {c.nombre}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        type="button"
                        onClick={() => setShowCategoryDialog(true)}
                        title="Crear categoría"
                        className="h-11 w-11 p-0 rounded-lg text-white bg-[#103254] hover:bg-[#0d2742] transition-colors shrink-0 flex items-center justify-center"
                      >
                        +
                      </Button>
                    </div>
                  </div>

                  {/* Icono */}
                  <div>
                    <label className="block font-medium mb-2 text-gray-700 dark:text-gray-300">
                      Icono
                    </label>
                    <IconPicker
                      value={form.icono || ''}
                      onChange={(name) =>
                        setForm((prev) => ({ ...prev, icono: name }))
                      }
                    />
                  </div>

                  {/* Descripción */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block font-medium text-gray-700 dark:text-gray-300">
                        {t('editServiceDialog.labelDescription')}
                      </label>
                      {form.servicio && (
                        <button
                          type="button"
                          onClick={handleGenerarDescripcion}
                          disabled={loadingIA}
                          className="flex items-center gap-1.5 text-xs font-medium text-[#103254] dark:text-blue-400 hover:text-[#0d2742] dark:hover:text-blue-300 border border-[#103254]/30 dark:border-blue-400/30 rounded-lg px-2.5 py-1 hover:bg-[#103254]/5 dark:hover:bg-blue-400/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                          {loadingIA ? 'Generando…' : 'Generar con IA'}
                        </button>
                      )}
                    </div>
                    <textarea
                      name="descripcion"
                      value={form.descripcion}
                      onChange={handleInput}
                      rows={3}
                      className="w-full border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg p-2 text-sm"
                    />
                  </div>

                  {/* Precio + duración */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-medium mb-1 text-gray-700 dark:text-gray-300">
                        {form.reserva_por_horas
                          ? t('editServiceDialog.labelPricePerHour')
                          : t('editServiceDialog.labelPrice')}
                      </label>
                      <Input
                        name="precio"
                        type="number"
                        value={form.precio}
                        onChange={handleInput}
                        className="dark:bg-white/5 dark:border-white/10 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block font-medium mb-1 text-gray-700 dark:text-gray-300">
                        {t('editServiceDialog.labelDuration')}
                      </label>
                      {form.reserva_por_horas ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400 pt-2">
                          {t('editServiceDialog.hourlyDurationHint')}
                        </p>
                      ) : (
                        <DurationPicker
                          value={form.duracion_minutos}
                          onChange={(val) =>
                            setForm((prev) => ({ ...prev, duracion_minutos: val }))
                          }
                        />
                      )}
                    </div>
                  </div>

                  {editingService && !form.reserva_por_horas && (
                    <Button
                      variant="outline"
                      onClick={() => onEditPhases(editingService.id_servicio)}
                      className="w-full rounded-lg border border-gray-300 dark:border-white/10 font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                    >
                      <Puzzle className="w-4 h-4 mr-2" />
                      {t('editServiceDialog.editPhases')}
                    </Button>
                  )}

                  {!editingService && !form.reserva_por_horas && (
                    <div className="pt-3 border-t border-gray-200 dark:border-white/10">
                      <label className="flex items-center font-medium mb-1 text-gray-700 dark:text-gray-300">
                        {t('editServiceDialog.labelConfigPhases')}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="ml-2 text-gray-500 dark:text-gray-400"
                          onClick={() => setShowInfoCrearFases(true)}
                        >
                          <AlertCircle className="w-4 h-4" />
                        </Button>
                      </label>
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={crearFasesAhora}
                          onCheckedChange={(checked) =>
                            setCrearFasesAhora(checked)
                          }
                        />
                        <span
                          className={
                            crearFasesAhora
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-gray-600 dark:text-gray-400'
                          }
                        >
                          {crearFasesAhora
                            ? t('editServiceDialog.yesLabel')
                            : t('editServiceDialog.noLabel')}
                        </span>
                      </div>
                      <InfoDialog
                        open={showInfoCrearFases}
                        onClose={() => setShowInfoCrearFases(false)}
                        title={t('editServiceDialog.labelConfigPhases')}
                        description={t('editServiceDialog.infoConfigPhases')}
                      />
                    </div>
                  )}

                  {/* Activo */}
                  <div>
                    <label className="flex items-center font-medium mb-1 text-gray-700 dark:text-gray-300">
                      {t('editServiceDialog.labelActive')}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="ml-2 text-gray-500 dark:text-gray-400"
                        onClick={() => setShowInfoActivo(true)}
                      >
                        <AlertCircle className="w-4 h-4" />
                      </Button>
                    </label>
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={form.activo}
                        onCheckedChange={(checked) =>
                          setForm((prev) => ({ ...prev, activo: checked }))
                        }
                      />
                      <span
                        className={
                          form.activo
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-red-500 dark:text-red-400'
                        }
                      >
                        {form.activo
                          ? t('editServiceDialog.activeYes')
                          : t('editServiceDialog.activeNo')}
                      </span>
                    </div>
                    <InfoDialog
                      open={showInfoActivo}
                      onClose={() => setShowInfoActivo(false)}
                      title={t('editServiceDialog.labelActive')}
                      description={getDescripcion('activo')}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="flex items-center justify-center gap-2 cursor-pointer text-sm font-medium text-[#103254] dark:text-blue-400 hover:underline w-full"
                  >
                    {showAdvanced ? (
                      <>
                        <ChevronUp className="w-4 h-4" />
                        {t('editServiceDialog.hideAdvanced')}
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4" />
                        {t('editServiceDialog.showAdvanced')}
                      </>
                    )}
                  </button>

                  {showAdvanced && (
                    <div ref={advancedRef} className="space-y-6">
                      {/* Servicio público */}
                      <div>
                        <label className="flex items-center font-medium mb-1 text-gray-700 dark:text-gray-300">
                          {t('editServiceDialog.labelPublic')}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="ml-2 text-gray-500 dark:text-gray-400"
                            onClick={() => setShowInfoPublico(true)}
                          >
                            <AlertCircle className="w-4 h-4" />
                          </Button>
                        </label>
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={form.servicio_publico}
                            onCheckedChange={(checked) =>
                              setForm((prev) => ({
                                ...prev,
                                servicio_publico: checked,
                              }))
                            }
                          />
                          <span
                            className={
                              form.servicio_publico
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-red-500 dark:text-red-400'
                            }
                          >
                            {form.servicio_publico
                              ? t('editServiceDialog.yesLabel')
                              : t('editServiceDialog.noLabel')}
                          </span>
                        </div>
                        <InfoDialog
                          open={showInfoPublico}
                          onClose={() => setShowInfoPublico(false)}
                          title={t('editServiceDialog.labelPublic')}
                          description={getDescripcion('servicio_publico')}
                        />
                      </div>

                      {/* Reservable online */}
                      {form.servicio_publico && (
                        <div>
                          <label className="flex items-center font-medium mb-1 text-gray-700 dark:text-gray-300">
                            {t('editServiceDialog.labelBookableOnline')}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="ml-2 text-gray-500 dark:text-gray-400"
                              onClick={() => setShowInfoReservable(true)}
                            >
                              <AlertCircle className="w-4 h-4" />
                            </Button>
                          </label>
                          <div className="flex items-center gap-3">
                            <Switch
                              checked={form.reservable_online}
                              onCheckedChange={(checked) =>
                                setForm((prev) => ({
                                  ...prev,
                                  reservable_online: checked,
                                }))
                              }
                            />
                            <span
                              className={
                                form.reservable_online
                                  ? 'text-green-600 dark:text-green-400'
                                  : 'text-red-500 dark:text-red-400'
                              }
                            >
                              {form.reservable_online
                                ? t('editServiceDialog.yesLabel')
                                : t('editServiceDialog.noLabel')}
                            </span>
                          </div>
                          <InfoDialog
                            open={showInfoReservable}
                            onClose={() => setShowInfoReservable(false)}
                            title={t('editServiceDialog.labelBookableOnline')}
                            description={t('editServiceDialog.infoBookableOnline')}
                          />
                        </div>
                      )}

                      {/* Reserva por horas */}
                      <div>
                        <label className="flex items-center font-medium mb-1 text-gray-700 dark:text-gray-300">
                          {t('editServiceDialog.labelHourly')}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="ml-2 text-gray-500 dark:text-gray-400"
                            onClick={() => setShowInfoPorHoras(true)}
                          >
                            <AlertCircle className="w-4 h-4" />
                          </Button>
                        </label>
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={form.reserva_por_horas}
                            onCheckedChange={(checked) => {
                              setForm((prev) => ({
                                ...prev,
                                reserva_por_horas: checked,
                              }));
                              if (checked) setCrearFasesAhora(false);
                            }}
                          />
                          <span
                            className={
                              form.reserva_por_horas
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-gray-600 dark:text-gray-400'
                            }
                          >
                            {form.reserva_por_horas
                              ? t('editServiceDialog.yesLabel')
                              : t('editServiceDialog.noLabel')}
                          </span>
                        </div>
                        <InfoDialog
                          open={showInfoPorHoras}
                          onClose={() => setShowInfoPorHoras(false)}
                          title={t('editServiceDialog.labelHourly')}
                          description={t('editServiceDialog.infoHourly')}
                        />
                      </div>

                      {form.reserva_por_horas && (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block font-medium mb-1 text-gray-700 dark:text-gray-300">
                              {t('editServiceDialog.labelMinHours')}
                            </label>
                            <Input
                              name="horas_min"
                              type="number"
                              min={1}
                              value={form.horas_min}
                              onChange={handleInput}
                              className="dark:bg-white/5 dark:border-white/10 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="block font-medium mb-1 text-gray-700 dark:text-gray-300">
                              {t('editServiceDialog.labelMaxHours')}
                            </label>
                            <Input
                              name="horas_max"
                              type="number"
                              min={1}
                              value={form.horas_max}
                              onChange={handleInput}
                              className="dark:bg-white/5 dark:border-white/10 dark:text-white"
                            />
                          </div>
                        </div>
                      )}

                      {/* Min / Max clientes */}
                      <div className="flex gap-4">
                        <div className="w-full">
                          <label className="block font-medium mb-1 text-gray-700 dark:text-gray-300">
                            {t('editServiceDialog.labelMinClients')}
                          </label>
                          <Input
                            name="cliente_min"
                            type="number"
                            value={form.cliente_min}
                            onChange={handleInput}
                            className="dark:bg-white/5 dark:border-white/10 dark:text-white"
                          />
                        </div>
                        <div className="w-full">
                          <label className="block font-medium mb-1 text-gray-700 dark:text-gray-300">
                            {t('editServiceDialog.labelMaxClients')}
                          </label>
                          <Input
                            name="cliente_max"
                            type="number"
                            value={form.cliente_max}
                            onChange={handleInput}
                            className="dark:bg-white/5 dark:border-white/10 dark:text-white"
                          />
                        </div>
                      </div>

                      {parseInt(form.cliente_max, 10) > 1 && (
                        <div>
                          <label className="block font-medium mb-1 text-gray-700 dark:text-gray-300">
                            {t('editServiceDialog.labelPriceApplies')}
                          </label>
                          <Select
                            value={form.precio_aplica}
                            onValueChange={(v) =>
                              setForm((prev) => ({ ...prev, precio_aplica: v }))
                            }
                          >
                            <SelectTrigger className="w-full h-9 text-sm focus:ring-[#103254]/30 dark:bg-white/5 dark:border-white/10 dark:text-white">
                              <SelectValue
                                placeholder={t(
                                  'editServiceDialog.placeholderPriceApplies'
                                )}
                              />
                            </SelectTrigger>
                            <SelectContent className="z-[70]">
                              {precioAplicaOptions.map((opt) => (
                                <SelectItem
                                  key={opt}
                                  value={opt}
                                  className="text-sm"
                                >
                                  {opt}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      <div>
                        <label className="flex items-center font-medium mb-1 text-gray-700 dark:text-gray-300">
                          {t('editServiceDialog.labelGroups')}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="ml-2 text-gray-500 dark:text-gray-400"
                            onClick={() => setShowInfoPermiteGrupos(true)}
                          >
                            <AlertCircle className="w-4 h-4" />
                          </Button>
                        </label>
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={form.permite_grupos}
                            onCheckedChange={(checked) =>
                              setForm((prev) => ({
                                ...prev,
                                permite_grupos: checked,
                              }))
                            }
                          />
                          <span
                            className={
                              form.permite_grupos
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-red-500 dark:text-red-400'
                            }
                          >
                            {form.permite_grupos
                              ? t('editServiceDialog.yesLabel')
                              : t('editServiceDialog.noLabel')}
                          </span>
                        </div>
                        <InfoDialog
                          open={showInfoPermiteGrupos}
                          onClose={() => setShowInfoPermiteGrupos(false)}
                          title={t('editServiceDialog.labelGroups')}
                          description={getDescripcion('permite_grupos')}
                        />
                      </div>

                      {/* Requiere trabajador */}
                      <div>
                        <label className="flex items-center font-medium mb-1 text-gray-700 dark:text-gray-300">
                          {t('editServiceDialog.labelWorker')}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="ml-2 text-gray-500 dark:text-gray-400"
                            onClick={() => setShowInfoRequiereTrabajador(true)}
                          >
                            <AlertCircle className="w-4 h-4" />
                          </Button>
                        </label>
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={form.requiere_trabajador}
                            onCheckedChange={(checked) =>
                              setForm((prev) => ({
                                ...prev,
                                requiere_trabajador: checked,
                              }))
                            }
                          />
                          <span
                            className={
                              form.requiere_trabajador
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-red-500 dark:text-red-400'
                            }
                          >
                            {form.requiere_trabajador
                              ? t('editServiceDialog.yesLabel')
                              : t('editServiceDialog.noLabel')}
                          </span>
                        </div>
                        <InfoDialog
                          open={showInfoRequiereTrabajador}
                          onClose={() => setShowInfoRequiereTrabajador(false)}
                          title={t('editServiceDialog.labelWorker')}
                          description={getDescripcion('requiere_trabajador')}
                        />
                      </div>

                      {/* Días disponibles */}
                      <div ref={daysRef}>
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <button
                            type="button"
                            onClick={() => setShowDays(!showDays)}
                            className="flex items-center gap-2 cursor-pointer text-sm font-medium text-[#103254] dark:text-blue-400 hover:underline"
                          >
                            {showDays ? (
                              <>
                                <ChevronUp className="w-4 h-4" />
                                {t('editServiceDialog.hideDays')}
                              </>
                            ) : (
                              <>
                                <ChevronDown className="w-4 h-4" />
                                {t('editServiceDialog.showDays')}
                              </>
                            )}
                          </button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-gray-500 dark:text-gray-400"
                            onClick={() => setShowInfoDiasDisponibles(true)}
                          >
                            <AlertCircle className="w-4 h-4" />
                          </Button>
                        </div>
                        <InfoDialog
                          open={showInfoDiasDisponibles}
                          onClose={() => setShowInfoDiasDisponibles(false)}
                          title={t('editServiceDialog.showDays')}
                        />
                        {showDays && (
                          <div className="space-y-2">
                            {DAYS.map((day) => (
                              <div
                                key={day}
                                className="flex items-center justify-between py-1"
                              >
                                <span className="capitalize text-gray-700 dark:text-gray-300">
                                  {day}
                                </span>
                                <Switch
                                  checked={form.dias_semana.includes(day)}
                                  onCheckedChange={() => toggleDay(day)}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Horario concreto */}
                      <div>
                        <label className="flex items-center font-medium mb-1 text-gray-700 dark:text-gray-300">
                          {t('editServiceDialog.labelSchedule')}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="ml-2 text-gray-500 dark:text-gray-400"
                            onClick={() => setShowInfoHorarioConcreto(true)}
                          >
                            <AlertCircle className="w-4 h-4" />
                          </Button>
                        </label>
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={form.horario_concreto}
                            onCheckedChange={(checked) =>
                              setForm((prev) => ({
                                ...prev,
                                horario_concreto: checked,
                              }))
                            }
                          />
                          <span
                            className={
                              form.horario_concreto
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-red-500 dark:text-red-400'
                            }
                          >
                            {form.horario_concreto
                              ? t('editServiceDialog.yesLabel')
                              : t('editServiceDialog.noLabel')}
                          </span>
                        </div>
                        <InfoDialog
                          open={showInfoHorarioConcreto}
                          onClose={() => setShowInfoHorarioConcreto(false)}
                          title={t('editServiceDialog.labelSchedule')}
                          description={getDescripcion('horario_concreto')}
                        />
                      </div>

                      {form.horario_concreto && (
                        <div ref={horariosRef} className="space-y-3">
                          <div className="flex gap-2">
                            <Input
                              type="time"
                              value={newHora}
                              onChange={(e) => setNewHora(e.target.value)}
                              className="min-w-0 appearance-none dark:bg-white/5 dark:border-white/10 dark:text-white"
                            />
                            <Button
                              onClick={addHora}
                              className="bg-[#103254] hover:bg-[#0d2742] text-white rounded-lg transition-colors"
                            >
                              {t('editServiceDialog.addTime')}
                            </Button>
                          </div>
                          {horas.length > 0 && (
                            <ul className="space-y-1">
                              {horas.map((h, idx) => (
                                <li
                                  key={idx}
                                  className="flex justify-between items-center bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-2 rounded-md"
                                >
                                  <span className="text-gray-900 dark:text-white">
                                    {h}
                                  </span>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => removeHora(idx)}
                                    className="text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                                  >
                                    ✖
                                  </Button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              )}

              {!showCategoryDialog && (
              <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
                <Button
                  variant="outline"
                  onClick={onClose}
                  className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
                >
                  {t('editServiceDialog.cancel')}
                </Button>
                <Button
                  onClick={handleSubmit}
                  className={`w-full sm:flex-1 ${DLG_BTN_PRIMARY}`}
                >
                  {editingService
                    ? t('editServiceDialog.saveChanges')
                    : t('editServiceDialog.createService')}
                </Button>
              </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ================================================
          DIÁLOGO POST-CREACIÓN
      ================================================= */}
      <Dialog
        open={showInfo}
        onOpenChange={() => {
          setShowInfo(false);
          onClose();
        }}
      >
        <DialogContent
          className="
            z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
            [&>button:last-child]:hidden
            flex flex-col
            !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
            w-full max-w-none rounded-t-2xl rounded-b-none
            data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
            sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
            sm:w-[95%] sm:max-w-sm sm:mx-auto sm:h-auto sm:rounded-xl sm:shadow-xl
          "
        >
          <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0">
            <DialogHeader>
              <div className="flex items-center gap-2">
                <AlertCircle className="w-6 h-6 text-yellow-500 dark:text-yellow-400" />
                <DialogTitle className="text-lg font-bold text-[#103254] dark:text-blue-400">
                  {t('editServiceDialog.infoTitle')}
                </DialogTitle>
              </div>
            </DialogHeader>
          </div>
          <div className="px-4 sm:px-6 pb-3">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t('editServiceDialog.infoCreated')}
              {form.requiere_trabajador && t('editServiceDialog.infoWorker')}
            </p>
          </div>
          <DialogFooter className="px-4 sm:px-6 pb-5 sm:pb-4 border-t border-gray-100 dark:border-white/10 pt-3">
            <Button
              onClick={() => {
                setShowInfo(false);
                onClose();
              }}
              className={`w-full ${DLG_BTN_PRIMARY}`}
            >
              {t('editServiceDialog.understood')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================
          IA - Generar descripción
      ================================================= */}
      <GenerateDescriptionDialog
        open={showIADialog}
        onOpenChange={setShowIADialog}
        descripcion={iaDescripcion}
        loading={loadingIA}
        onAccept={() => {
          setForm((prev) => ({ ...prev, descripcion: iaDescripcion }));
          setShowIADialog(false);
        }}
        onRegenerate={handleSiguienteVariante}
      />

      {/* ================================================
          ASIGNACIÓN DE TRABAJADORES
      ================================================= */}
      <AssignWorkersToServiceDialog
        open={assignWorkersOpen}
        idServicio={servicioRecienCreado}
        onClose={handleCloseAssignWorkers}
      />
    </>
  );
};